/**
 * shadcn/ui Switch, over the Radix primitive already bundled in `radix-ui` (§4.1, §5.13 — no new
 * dependency).
 *
 * **Sizes come from the Tailwind scale, not shadcn's generated arbitrary values** — the upstream
 * snippet ships `h-[1.15rem]` and `ring-[3px]`, which §4.8 rules out. The thumb is `bg-background`
 * rather than always-white so it stays legible on the checked track in dark mode.
 */

import { Switch as SwitchPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

function Switch({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent',
        'shadow-xs transition-colors outline-none',
        'bg-input data-[state=checked]:bg-primary',
        // Visible focus, on the keyboard path every report control has to be reachable by (§4.9).
        'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block size-4 rounded-full bg-background shadow-sm ring-0',
          'transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
